/**
 * Converts phone numbers in text/HTML to clickable tel: links
 *
 * Detects numeric phone numbers in various formats and converts them to clickable links.
 * Supports formats including:
 * - (703) 836-7112
 * - 800-771-2303
 * - +1-800-733-2767
 * - (800) 422-4828
 * - 301-530-6500
 *
 * @param text - The input string containing phone numbers
 * @returns String with phone numbers converted to clickable tel: links
 */

import { findPhoneNumbersInText } from './phoneNumber';

export const linkifyPhoneNumbersString = (text: string): string => {
  if (!text) return text;

  try {
    // Replace commas with a very uncommon Unicode character to prevent boundary issues - this fixes a bug where
    // commas in the text would be treated as phone number separators, causing the string below to return invalid format
    // Contact: (703) 836-7112, 800-771-2303, or +1-805-964-4767
    const RARE_CHAR = '\uE000'; // Private Use Area character, extremely unlikely to appear in text
    const escapedText = text.replace(/,/g, RARE_CHAR);

    // Find phone numbers in the escaped text
    const numbers = findPhoneNumbersInText(escapedText, { defaultCountry: 'US', extended: true });

    if (numbers.length === 0) return text;

    // Pre-compute URL/link boundaries once instead of recomputing per phone number match
    const urlPattern = /((?:https?|ftp):\/\/[^\s<>"']+)/gi;
    const urlSpans = Array.from(text.matchAll(urlPattern), (match) => ({
      end: (match.index ?? 0) + match[0].length,
      start: match.index ?? 0,
    }));

    // Markdown links like `[label](url)` — phone numbers inside either the label or the URL
    // must be skipped, otherwise we'd nest links (e.g. `[[+1 ...](tel:+1...)](tel:+1...)`).
    const markdownLinkPattern = /\[[^\]]*\]\([^)]*\)/g;
    const markdownLinkSpans = Array.from(text.matchAll(markdownLinkPattern), (match) => ({
      end: (match.index ?? 0) + match[0].length,
      start: match.index ?? 0,
    }));

    // Process from end to start to maintain correct string positions, as we are augmenting the size of the found phone number slice
    // Since RARE_CHAR is same length as comma, positions should map correctly
    return numbers.reduceRight((result, { endsAt, number, startsAt }) => {
      // Get the original text (with commas restored)
      const originalText = text.substring(startsAt, endsAt);

      // Check if this phone number is already inside an HTML anchor tag
      const beforeMatch = text.substring(0, startsAt);
      const insideHtmlAnchor = beforeMatch.lastIndexOf('<a') > beforeMatch.lastIndexOf('</a>');

      if (insideHtmlAnchor) {
        return result;
      }

      const insideUrl = urlSpans.some(({ end, start }) => startsAt >= start && startsAt < end);

      if (insideUrl) {
        return result;
      }

      const insideMarkdownLink = markdownLinkSpans.some(({ end, start }) => startsAt >= start && startsAt < end);

      if (insideMarkdownLink) {
        return result;
      }

      // Create tel: link using E.164 format from the PhoneNumber object
      const telLink = `<a href="tel:${number.number}">${originalText}</a>`;

      // Replace the original text with the tel: link
      return result.substring(0, startsAt) + telLink + result.substring(endsAt);
    }, text);
  } catch (error) {
    // If libphonenumber-js fails, return original text
    return text;
  }
};

export const linkifyHtmlPhoneNumbersToMarkdown = (
  text: string,
  { convertToHtmlLinks = linkifyPhoneNumbersString }: { convertToHtmlLinks?: (text: string) => string } = {},
): string => {
  if (!text) return text;

  const linkedHtml = convertToHtmlLinks(text);

  return linkedHtml.replace(
    /<a href="tel:([^"]+)">([^<]*)<\/a>/gi,
    (_match, telTarget, label) => `[${label}](tel:${telTarget})`,
  );
};
